import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  isRouteErrorResponse,
} from "@remix-run/react";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useEffect } from "react";
import { AuthProvider } from "~/lib/auth-context";
import { RecipeMetadataProvider } from "~/lib/recipe-metadata-context";
import { getAuthUser } from "~/services/auth.server";
import { MainLayout } from "~/components/layout";
import { PerformanceMonitor } from "~/lib/performance";
import { ToastProvider } from "~/components/ui/Toast";

import "./tailwind.css";

// 전역 타입 선언 추가
declare global {
  interface Window {
    ENV: {
      SUPABASE_URL: string;
      SUPABASE_ANON_KEY: string;
    };
  }
}

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:ital,wght@0,100..900;1,100..900&family=Noto+Serif+KR:wght@200..900&display=swap",
  },
  // PWA manifest
  { rel: "manifest", href: "/manifest.json" },
  // Preload critical resources
  { rel: "dns-prefetch", href: "https://supabase.com" },
  // Apple touch icons
  {
    rel: "apple-touch-icon",
    href: "/app-icon.png",
  },
  {
    rel: "apple-touch-icon",
    sizes: "192x192",
    href: "/icons/icon-192x192.png",
  },
  {
    rel: "apple-touch-icon",
    sizes: "144x144",
    href: "/icons/icon-144x144.png",
  },
  // Favicon
  {
    rel: "icon",
    type: "image/x-icon",
    href: "/favicon.ico",
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "192x192",
    href: "/icons/icon-192x192.png",
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "144x144",
    href: "/icons/icon-144x144.png",
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthUser(request);

  return json({
    user,
    ENV: {
      SUPABASE_URL: process.env.SUPABASE_URL,
      SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
    },
  });
}

// Document 컴포넌트 분리 - 공통 HTML 구조
function Document({
  children,
  title = "저속노화 레시피 - 슬로우다이브",
  env = {},
}: {
  children: React.ReactNode;
  title?: string;
  env?: Record<string, any>;
}) {
  return (
    <html lang="ko">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no"
        />
        <meta name="theme-color" content="#3B82F6" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="슬로우다이브" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="msapplication-TileColor" content="#3B82F6" />
        <meta name="msapplication-tap-highlight" content="no" />
        <title>{title}</title>
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
      </head>
      <body className="bg-neutral-50 dark:bg-neutral-950">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// 기본 앱 컴포넌트
export default function App() {
  const data = useLoaderData<typeof loader>();

  // Initialize performance monitoring and PWA
  useEffect(() => {
    const monitor = PerformanceMonitor.getInstance();
    monitor.init();

    // Log performance report in development
    if (
      typeof process !== "undefined" &&
      process.env.NODE_ENV === "development"
    ) {
      setTimeout(() => {
        monitor.logReport();
      }, 5000);
    }

    // Register service worker for PWA (only in production to avoid double registration in React StrictMode)
    if (
      typeof process !== "undefined" &&
      process.env.NODE_ENV === "production" &&
      "serviceWorker" in navigator
    ) {
      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => {
          console.log("Service Worker registered:", registration);

          // Check for updates
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (
                  newWorker.state === "installed" &&
                  navigator.serviceWorker.controller
                ) {
                  // New version available
                  if (
                    confirm("새로운 버전이 있습니다. 업데이트하시겠습니까?")
                  ) {
                    newWorker.postMessage({ type: "SKIP_WAITING" });
                    window.location.reload();
                  }
                }
              });
            }
          });
        })
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });

      // Handle service worker messages
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data && event.data.type === "SW_UPDATED") {
          window.location.reload();
        }
      });
    }

    // Initialize offline storage
    if (typeof window !== "undefined") {
      import("~/lib/offline-storage").then(({ offlineStorage }) => {
        offlineStorage.init().catch(console.error);
      });
    }
  }, []);

  return (
    <Document env={data.ENV}>
      <AuthProvider initialUser={data.user}>
        <RecipeMetadataProvider>
          <ToastProvider>
            <MainLayout>
              <Outlet />
            </MainLayout>
          </ToastProvider>
        </RecipeMetadataProvider>
      </AuthProvider>
    </Document>
  );
}

// 에러 바운더리 - useLoaderData를 사용하지 않음
export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <Document title={`${error.status} - 오류 발생`}>
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
              {error.status}
            </h1>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-8">
              {error.status === 404
                ? "페이지를 찾을 수 없습니다."
                : error.statusText}
            </p>
            <a
              href="/"
              className="bg-primary-500 text-white px-4 py-2 rounded hover:bg-primary-600 transition-colors"
            >
              홈으로 돌아가기
            </a>
          </div>
        </div>
      </Document>
    );
  }

  return (
    <Document title="오류 발생">
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
            오류가 발생했습니다
          </h1>
          <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-8">
            잠시 후 다시 시도해주세요.
          </p>
          <a
            href="/"
            className="bg-primary-500 text-white px-4 py-2 rounded hover:bg-primary-600 transition-colors"
          >
            홈으로 돌아가기
          </a>
        </div>
      </div>
    </Document>
  );
}
